/**
 * BookingQuestionFields: the one booking-question renderer in the React tree.
 *
 * Mounted by WalkInJourney (join.) and by the Designer (book. / events., and
 * the Studio previews of both). It replaces the two renderers those files
 * carried, which had drifted apart.
 *
 * DOM = the styling contract's generic field vocabulary, the same nesting the
 * server renderer emits (portal-html-renderer event-register-inline.ts), so one
 * tenant stylesheet styles both paths:
 *
 *   .jrni-form-field[data-required]             <div>, or <fieldset> for a group
 *     .jrni-form-label[data-required]           <label for>, or <legend>
 *     .jrni-form-input                          input / textarea / select
 *     | .jrni-form-choices > .jrni-form-choice  radio / checkbox rows
 *     | .jrni-form-file + .jrni-form-file-list  drop zone over a real file input
 *     .jrni-form-help
 *     .jrni-error                               role="alert"
 *   .jrni-form-note[data-kind=heading|text]     heading / label question types
 *
 * NO INLINE STYLE on any of it. The default look is BOOKING_QUESTIONS_CSS,
 * inserted once into <head> inside `@layer jrni`; a tenant sheet goes in
 * `@layer site` (lib/tenant-journey-css.ts), so a tenant rule wins without
 * !important, the same order PLATFORM_CSS uses on the server. Classes prefixed
 * `jrni-x-` are internal and not contract surface.
 *
 * The renderer holds no answers and makes no requests. The host supplies the
 * value, the error text, and the upload (`pickFiles`). The upload path is the
 * host's own (createJourneyQuestionFileUpload / createQuestionFileUpload ->
 * uploadQuestionFileContent).
 *
 * Data: n/a (presentational). The hosts' upload ops are unchanged by this file.
 */
import React from "react";
import { t, useLocaleVersion } from "../../lib/i18n";
import { QUESTION_FILE_FORMAT_ACCEPT, QUESTION_FILE_SIZE_TIERS_MB } from "@v0y4ge/api-contracts";
import { isMultiChoice, isReadOnly, otherOption, shownOptions } from "../questions/public-question-flow";

export interface BookingQuestionOption { id: string; label: string; allowsText?: boolean }
export interface BookingStepperRowDef { id: string; label?: string; helper?: string; min?: number; max?: number }
export interface BookingQuestion {
  id: string;
  label: string;
  type: string;
  required?: boolean;
  help?: string | null;
  placeholder?: string | null;
  options?: BookingQuestionOption[];
  stepper?: { min?: number; max?: number; allowMultiple?: boolean; rows?: BookingStepperRowDef[] } | null;
  file?: { formats?: string[]; maxSize?: string | number; allowMultiple?: boolean } | null;
  info?: { tone?: string; title?: string } | null;
  textRules?: { readOnly?: boolean; validationEnabled?: boolean; maxLength?: number } | null;
  number?: { decimals?: number; unit?: string } | null;
  date?: { notInPast?: boolean; earliest?: string | null; latest?: string | null } | null;
  select?: { allowMultiple?: boolean; sortAlphabetically?: boolean } | null;
  yesno?: { readOnly?: boolean; startsAs?: string } | null;
}

export interface BookingQuestionFileState { files: File[]; busy: boolean; error?: string }

export interface BookingQuestionsHost {
  /** The current answer, including any starting answer the host applies. */
  value(q: BookingQuestion): unknown;
  set(id: string, v: unknown): void;
  /** The one error line for this question (required, rule, server refusal, upload), or nothing. */
  error(q: BookingQuestion): string | null | undefined;
  file?(q: BookingQuestion): BookingQuestionFileState | null;
  /** The full set of files the question should now hold; [] clears it. The host uploads and sets the ids. */
  pickFiles?(q: BookingQuestion, files: File[]): void;
  /** Present only on hosts that collect the "Other" option's text. */
  otherText?(q: BookingQuestion): string;
  setOtherText?(q: BookingQuestion, text: string): void;
}

export interface BookingQuestionFieldsProps {
  questions: BookingQuestion[];
  host: BookingQuestionsHost;
  /** Prefix for every id this renders, unique per form on the page. */
  idPrefix: string;
  testid?: string;
}

/* ── shared helpers (both hosts read these, so they cannot drift) ─────────── */

/** A file question's picker settings from its own config: accept list, size ceiling, one file or several. */
export function questionFileConfig(q: BookingQuestion | null | undefined): { accept?: string; multiple: boolean; maxMb: number; maxBytes: number } {
  const fc = q && q.file && typeof q.file === "object" ? q.file : null;
  const table = (QUESTION_FILE_FORMAT_ACCEPT || {}) as unknown as Record<string, { extensions?: string[]; mimeTypes?: string[] } | undefined>;
  const formats = fc && Array.isArray(fc.formats) ? fc.formats : [];
  const accept = formats.reduce<string[]>((out, id) => {
    const a = table[id];
    if (a) return out.concat((a.extensions || []).map((x) => "." + String(x).replace(/^\./, "")), a.mimeTypes || []);
    const raw = String(id || "").trim();
    return raw ? out.concat([raw.startsWith(".") || raw.includes("/") ? raw : "." + raw]) : out;
  }, []).join(",");
  const tiers = (QUESTION_FILE_SIZE_TIERS_MB || {}) as unknown as Record<string, number | undefined>;
  let maxBytes = 0;
  let maxMb = 0;
  if (fc && typeof fc.maxSize === "number" && fc.maxSize > 0) { maxBytes = fc.maxSize; maxMb = Math.round((fc.maxSize / (1024 * 1024)) * 10) / 10; }
  else if (fc) { maxMb = tiers[String(fc.maxSize || "medium")] || 0; maxBytes = maxMb ? maxMb * 1024 * 1024 : 0; }
  return { accept: accept || undefined, multiple: !!(fc && fc.allowMultiple), maxMb, maxBytes };
}

/** A file over the question's ceiling, as the field's error text; null when every file fits. */
export function questionFileTooLarge(q: BookingQuestion, files: File[]): string | null {
  const cfg = questionFileConfig(q);
  const big = cfg.maxBytes ? files.find((f) => f.size > cfg.maxBytes) : undefined;
  if (!big) return null;
  return t("journeys.bookingQuestions.fileTooLargeMb", "{name} is larger than {mb} MB.", { name: big.name, mb: String(cfg.maxMb) });
}

/** The counter rows a stepper renders: every served row with allowMultiple, else the first; a question served
 *  without row ids is one count (key "single", a scalar answer), held to the question's own bounds. */
/** The earliest date a date question accepts, as YYYY-MM-DD (CC review, 2026-10-18).
 *  "Not in the past" means today in the visitor's OWN calendar (built from the local
 *  year, month and day; toISOString() is the UTC date, a day off for part of the day in
 *  many zones). When both rules apply the LATER one wins, so an author's future
 *  earliest date is never loosened to today. Neither rule: no minimum. */
export function questionDateMin(q: BookingQuestion, now: Date = new Date()): string | undefined {
  const d = q.date;
  if (!d) return undefined;
  const earliest = typeof d.earliest === "string" && /^\d{4}-\d{2}-\d{2}/.test(d.earliest) ? d.earliest.slice(0, 10) : undefined;
  if (!d.notInPast) return earliest;
  const today = now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0") + "-" + String(now.getDate()).padStart(2, "0");
  return earliest && earliest > today ? earliest : today;
}

export function bookingStepperRows(q: BookingQuestion): (BookingStepperRowDef & { key: string })[] {
  const st = q.stepper || null;
  const rows = st && Array.isArray(st.rows) ? st.rows.filter((r) => r && r.id) : [];
  if (rows.length) return (st && st.allowMultiple ? rows : rows.slice(0, 1)).map((r) => ({ ...r, key: r.id }));
  return [{ id: "single", key: "single", label: "", min: st?.min, max: st?.max }];
}

function clampCount(s: string, min?: number, max?: number): string {
  if (!/^\d+$/.test(s)) return "";
  let n = parseInt(s, 10);
  const lo = typeof min === "number" ? Math.max(0, min) : 0;
  if (n < lo) n = lo;
  if (typeof max === "number" && n > max) n = max;
  return String(n);
}

const fileKey = (f: File) => f.name + ":" + f.size + ":" + f.lastModified;

/* ── the default sheet ─────────────────────────────────────────────────────── */

/** Mirrors the jrni-ui Field + Input look (40px box, neutral-4 border, radius-default, 13px label, red asterisk,
 *  12px hint/error) so questions match the contact fields above them. Colour and font sizes are set on the field
 *  because the app's reset gives controls `color/font: inherit` outside any layer. */
export const BOOKING_QUESTIONS_CSS = `@layer jrni, site;
@layer jrni {
  .jrni-x-booking-questions { display: grid; gap: 14px; }
  .jrni-form-field { display: grid; gap: 6px; margin: 0; padding: 0; border: 0; min-inline-size: 0; font-family: var(--jrni-font-family-sans); font-size: var(--jrni-font-size-P1); color: var(--jrni-color-neutral-1); }
  .jrni-form-label { padding: 0; font-size: 13px; font-weight: 500; line-height: 1.4; color: var(--jrni-color-neutral-1); }
  .jrni-form-label[data-required="true"]::after { content: "*"; margin-inline-start: 2px; color: var(--jrni-color-semantic-red-1); }
  .jrni-form-input { box-sizing: border-box; inline-size: 100%; min-block-size: 40px; padding: 0 12px; margin: 0; line-height: 1.4; background: var(--jrni-color-base-white); border: 1px solid var(--jrni-color-neutral-4); border-radius: var(--jrni-radius-default); transition: border-color 120ms, box-shadow 120ms; }
  textarea.jrni-form-input { padding-block: 9px; min-block-size: 96px; resize: vertical; }
  select.jrni-form-input { appearance: none; padding-inline-end: 36px; cursor: pointer;
    background-image: linear-gradient(45deg, transparent 50%, var(--jrni-color-neutral-2) 50%), linear-gradient(135deg, var(--jrni-color-neutral-2) 50%, transparent 50%);
    background-position: calc(100% - 18px) 50%, calc(100% - 13px) 50%; background-size: 5px 5px; background-repeat: no-repeat; }
  .jrni-form-input:focus { outline: none; border-color: var(--jrni-color-primary-1); box-shadow: 0 0 0 1px var(--jrni-color-utility-focus); }
  .jrni-form-input[readonly], .jrni-form-input:disabled { background-color: var(--jrni-color-neutral-6); color: var(--jrni-color-neutral-2); }
  .jrni-form-input[aria-invalid="true"], .jrni-form-field[aria-invalid="true"] .jrni-form-choices .jrni-form-input { border-color: var(--jrni-color-semantic-red-1); }
  .jrni-form-input[aria-invalid="true"]:focus { box-shadow: 0 0 0 1px var(--jrni-color-semantic-red-1); }
  .jrni-form-choices { display: grid; gap: 8px; }
  .jrni-form-choice { display: flex; align-items: center; gap: 8px; min-block-size: 24px; }
  .jrni-form-choice input { inline-size: 16px; block-size: 16px; margin: 0; accent-color: var(--jrni-color-primary-1); flex: none; }
  .jrni-form-choice label { cursor: pointer; }
  .jrni-form-help { margin: 0; font-size: 12px; line-height: 1.4; color: var(--jrni-color-text-soft); }
  .jrni-form-field > .jrni-error { margin: 0; padding: 0; background: none; border: 0; font-size: 12px; line-height: 1.4; color: var(--jrni-color-semantic-red-1); }
  .jrni-form-note { margin: 0; font-family: var(--jrni-font-family-sans); color: var(--jrni-color-neutral-1); }
  .jrni-form-note[data-kind="heading"] { margin-block-start: 8px; font-size: var(--jrni-font-size-H3); font-weight: 600; }
  .jrni-form-note[data-kind="text"] { font-size: var(--jrni-font-size-P1); line-height: 1.5; }
  .jrni-form-note[data-tone] { padding: 10px 12px; border: 1px solid currentColor; border-radius: var(--jrni-radius-default); }
  .jrni-form-note[data-tone="info"] { color: var(--jrni-color-text-info, #2C5BD0); }
  .jrni-form-note[data-tone="success"] { color: var(--jrni-color-text-success, #027D47); }
  .jrni-form-note[data-tone="warning"] { color: var(--jrni-color-text-warning, #916D0F); }
  .jrni-form-note[data-tone="error"] { color: var(--jrni-color-text-danger, #B42318); }
  .jrni-form-note > strong { display: block; }
  .jrni-x-rows { display: grid; gap: 10px; }
  .jrni-x-row { display: grid; gap: 4px; }
  .jrni-x-row > .jrni-form-input { max-inline-size: 120px; }
  .jrni-x-unit { display: flex; align-items: center; gap: 8px; }
  .jrni-x-unit > .jrni-form-input { flex: 1 1 auto; }
  .jrni-form-file { position: relative; display: grid; justify-items: center; gap: 4px; padding: 20px 16px; text-align: center; border: 2px dashed var(--jrni-color-neutral-4); border-radius: var(--jrni-radius-default); background: var(--jrni-color-neutral-6); color: var(--jrni-color-neutral-2); font-size: 13px; transition: background 120ms, border-color 120ms; }
  .jrni-form-file:hover, .jrni-form-file[data-dragover="true"] { border-color: var(--jrni-color-primary-1); background: var(--jrni-color-primary-4); }
  .jrni-form-file:focus-within { border-color: var(--jrni-color-primary-1); box-shadow: 0 0 0 1px var(--jrni-color-utility-focus); }
  .jrni-form-file[data-disabled="true"] { cursor: progress; opacity: 0.7; }
  .jrni-form-file[aria-invalid="true"] { border-color: var(--jrni-color-semantic-red-1); }
  .jrni-form-file > input[type="file"] { position: absolute; inset: 0; inline-size: 100%; block-size: 100%; margin: 0; opacity: 0; cursor: pointer; font-size: 0; }
  .jrni-x-file-title { font-weight: 600; color: var(--jrni-color-neutral-1); }
  .jrni-x-file-browse { color: var(--jrni-color-primary-1); text-decoration: underline; }
  .jrni-form-file-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 6px; }
  .jrni-form-file-list > li { display: flex; align-items: center; gap: 12px; padding: 8px 12px; background: var(--jrni-color-base-white); border: 1px solid var(--jrni-color-neutral-5); border-radius: var(--jrni-radius-default); font-size: 13px; }
  .jrni-x-file-name { flex: 1 1 auto; min-inline-size: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 500; }
  .jrni-x-file-status { font-size: 12px; color: var(--jrni-color-text-soft); }
  .jrni-form-file-list progress { inline-size: 64px; block-size: 4px; accent-color: var(--jrni-color-primary-1); }
  .jrni-x-file-remove { inline-size: 28px; block-size: 28px; display: inline-flex; align-items: center; justify-content: center; border-radius: var(--jrni-radius-default); color: var(--jrni-color-neutral-2); font-size: 16px; line-height: 1; }
  .jrni-x-file-remove:hover { background: var(--jrni-color-neutral-6); }
  .jrni-x-visually-hidden { position: absolute; inline-size: 1px; block-size: 1px; margin: -1px; padding: 0; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
}
`;

const STYLE_ID = "jrni-booking-questions-css";
/** Inserted FIRST in <head>, so the layer order statement precedes any tenant sheet's. */
export function ensureBookingQuestionStyles(): void {
  if (typeof document === "undefined" || document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = BOOKING_QUESTIONS_CSS;
  document.head.insertBefore(el, document.head.firstChild);
}

/* ── the renderer ──────────────────────────────────────────────────────────── */

type Ids = { field: string; help: string; error: string };

function Shell(props: { q: BookingQuestion; ids: Ids; group: boolean; error: string; help: React.ReactNode; children: React.ReactNode }) {
  const { q, ids, group, error, help, children } = props;
  const req = q.required ? "true" : undefined;
  const sr = q.required ? <span className="jrni-x-visually-hidden">{" " + t("journeys.bookingQuestions.requiredMark", "(required)")}</span> : null;
  const describedBy = [help ? ids.help : "", error ? ids.error : ""].filter(Boolean).join(" ") || undefined;
  const tail = (<>
    {help ? <p className="jrni-form-help" id={ids.help}>{help}</p> : null}
    {error ? <p className="jrni-error" id={ids.error} role="alert">{error}</p> : null}
  </>);
  if (group) {
    return (
      <fieldset className="jrni-form-field" data-required={req} data-question-type={q.type} aria-invalid={error ? true : undefined} aria-describedby={describedBy}>
        <legend className="jrni-form-label" data-required={req}>{q.label}{sr}</legend>
        {children}
        {tail}
      </fieldset>
    );
  }
  return (
    <div className="jrni-form-field" data-required={req} data-question-type={q.type}>
      <label className="jrni-form-label" data-required={req} htmlFor={ids.field}>{q.label}{sr}</label>
      {children}
      {tail}
    </div>
  );
}

function FileField(props: { q: BookingQuestion; host: BookingQuestionsHost; ids: Ids; describedBy?: string; invalid: boolean }) {
  const { q, host, ids, describedBy, invalid } = props;
  const cfg = questionFileConfig(q);
  const st = host.file ? host.file(q) : null;
  const files = st ? st.files : [];
  const busy = !!(st && st.busy);
  const [over, setOver] = React.useState(false);
  const pick = (next: File[]) => { if (host.pickFiles) host.pickFiles(q, next); };
  const add = (list: File[]) => {
    if (!list.length) return;
    const merged = cfg.multiple ? files.concat(list.filter((f) => !files.some((x) => fileKey(x) === fileKey(f)))) : list.slice(0, 1);
    pick(merged);
  };
  const names = files.map((f) => f.name).join(", ");
  return (<>
    <div className="jrni-form-file" data-dragover={over ? "true" : undefined} data-disabled={busy ? "true" : undefined} aria-invalid={invalid ? true : undefined}
      onDragEnter={() => setOver(true)} onDragLeave={() => setOver(false)} onDrop={() => setOver(false)}>
      <span className="jrni-x-file-title" aria-hidden="true">
        {cfg.multiple ? t("journeys.bookingQuestions.fileDropMany", "Drag files here") : t("journeys.bookingQuestions.fileDrop", "Drag a file here")}
      </span>
      <span aria-hidden="true">{t("journeys.bookingQuestions.fileOr", "or")} <span className="jrni-x-file-browse">{t("journeys.bookingQuestions.fileBrowse", "browse")}</span></span>
      <input id={ids.field} type="file" accept={cfg.accept} multiple={cfg.multiple} disabled={busy || !host.pickFiles}
        aria-required={q.required ? true : undefined} aria-invalid={invalid ? true : undefined} aria-describedby={describedBy}
        onChange={(e) => { const list = e.target.files ? Array.from(e.target.files) : []; e.target.value = ""; add(list); }} />
    </div>
    {files.length ? (
      <ul className="jrni-form-file-list" aria-label={q.label}>
        {files.map((f) => (
          <li key={fileKey(f)}>
            <span className="jrni-x-file-name">{f.name}</span>
            {busy ? <progress aria-label={t("journeys.walkIn.uploading", "Uploading {names}…", { names })}></progress> : null}
            <span className="jrni-x-file-status">
              {busy ? t("journeys.bookingQuestions.fileUploading", "Uploading…") : st && st.error ? "" : t("journeys.bookingQuestions.fileUploaded", "Uploaded")}
            </span>
            <button type="button" className="jrni-x-file-remove" disabled={busy}
              aria-label={t("journeys.bookingQuestions.fileRemove", "Remove {name}", { name: f.name })}
              onClick={() => pick(files.filter((x) => fileKey(x) !== fileKey(f)))}>×</button>
          </li>
        ))}
      </ul>
    ) : null}
  </>);
}

export function BookingQuestionFields({ questions, host, idPrefix, testid }: BookingQuestionFieldsProps) {
  useLocaleVersion();
  if (typeof document !== "undefined") ensureBookingQuestionStyles();
  if (!questions.length) return null;
  return (
    <div className="jrni-x-booking-questions" data-testid={testid}>
      {questions.map((q) => <QuestionField key={q.id} q={q} host={host} idPrefix={idPrefix} />)}
    </div>
  );
}

function QuestionField({ q, host, idPrefix }: { q: BookingQuestion; host: BookingQuestionsHost; idPrefix: string }) {
  const base = idPrefix + q.id;
  const ids: Ids = { field: base, help: base + "-help", error: base + "-error" };

  if (q.type === "heading") return <h3 className="jrni-form-note" data-kind="heading">{q.label}</h3>;
  if (q.type === "label") {
    const tone = q.info && q.info.tone && q.info.tone !== "plain" ? q.info.tone : undefined;
    return (
      <div className="jrni-form-note" data-kind="text" data-tone={tone} role={tone === "error" || tone === "warning" ? "alert" : tone ? "note" : undefined}>
        {tone && q.info && q.info.title ? <strong>{q.info.title}</strong> : null}
        {q.label}
      </div>
    );
  }

  const error = String(host.error(q) || "");
  const invalid = !!error;
  const value = host.value(q);
  const ro = isReadOnly(q as never);
  const opts = shownOptions(q as never) as BookingQuestionOption[];
  const oth = host.otherText && host.setOtherText ? otherOption(q as never) : null;
  const cap = q.textRules && q.textRules.validationEnabled && q.textRules.maxLength ? q.textRules.maxLength : undefined;
  const set = (v: unknown) => host.set(q.id, v);
  const str = value === undefined || value === null || typeof value === "object" ? "" : String(value);
  const fileState = q.type === "file" && host.file ? host.file(q) : null;
  const fileNote = fileState && fileState.files.length && !fileState.error
    ? (fileState.busy
      ? t("journeys.walkIn.uploading", "Uploading {names}…", { names: fileState.files.map((f) => f.name).join(", ") })
      : t("journeys.walkIn.uploaded", "{names} uploaded.", { names: fileState.files.map((f) => f.name).join(", ") }))
    : "";
  const helpText = [q.help || "", fileNote].filter(Boolean).join(" ");
  const help = helpText || null;
  const describedBy = [help ? ids.help : "", error ? ids.error : ""].filter(Boolean).join(" ") || undefined;
  const common = {
    className: "jrni-form-input", id: ids.field,
    "aria-required": q.required ? true : undefined, "aria-invalid": invalid ? true : undefined, "aria-describedby": describedBy,
  };

  const multi = isMultiChoice(q as never);
  const picked: string[] = Array.isArray(value) ? (value as unknown[]).map(String) : str ? [str] : [];
  const otherBox = oth && picked.includes(oth.id) ? (
    <input className="jrni-form-input" type="text" maxLength={500}
      aria-label={t("questions.runtime.otherPlaceholder", "Please specify")} placeholder={t("questions.runtime.otherPlaceholder", "Please specify")}
      value={host.otherText ? host.otherText(q) : ""} onChange={(e) => host.setOtherText && host.setOtherText(q, e.target.value)} />
  ) : null;

  const choiceGroup = (kind: "radio" | "checkbox", rows: { id: string; label: string }[], isOn: (id: string) => boolean, onPick: (id: string, on: boolean) => void) => (
    <div className="jrni-form-choices" role={kind === "radio" ? "radiogroup" : undefined} aria-required={kind === "radio" && q.required ? true : undefined}>
      {rows.map((o, i) => {
        const cid = base + "-o" + i;
        return (
          <div className="jrni-form-choice" key={o.id}>
            <input type={kind} id={cid} name={base} value={o.id} checked={isOn(o.id)} disabled={ro}
              onChange={(e) => onPick(o.id, e.target.checked)} />
            <label htmlFor={cid}>{o.label}</label>
          </div>
        );
      })}
    </div>
  );

  if (q.type === "yesno") {
    const cur = value === true ? "yes" : value === false ? "no" : "";
    return (
      <Shell q={q} ids={ids} group={true} error={error} help={help}>
        {choiceGroup("radio", [{ id: "yes", label: t("journeys.walkInJourney.text10", "Yes") }, { id: "no", label: t("journeys.walkInJourney.text11", "No") }],
          (id) => cur === id, (id) => set(id === "yes"))}
      </Shell>
    );
  }
  if (multi) {
    return (
      <Shell q={q} ids={ids} group={true} error={error} help={help}>
        {choiceGroup("checkbox", opts, (id) => picked.includes(id), (id, on) => set(on ? picked.concat(picked.includes(id) ? [] : [id]) : picked.filter((x) => x !== id)))}
        {otherBox}
      </Shell>
    );
  }
  if (q.type === "radio") {
    return (
      <Shell q={q} ids={ids} group={true} error={error} help={help}>
        {choiceGroup("radio", opts, (id) => picked[0] === id, (id) => set(id))}
        {otherBox}
      </Shell>
    );
  }
  if (q.type === "stepper") {
    const rows = bookingStepperRows(q);
    const map = value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
    const rowValue = (key: string) => (key === "single" ? str : map[key] === undefined || map[key] === null ? "" : String(map[key]));
    const setRow = (key: string, v: string) => {
      if (key === "single") { set(v); return; }
      const now = host.value(q);
      const cur = now && typeof now === "object" && !Array.isArray(now) ? (now as Record<string, unknown>) : {};
      set({ ...cur, [key]: v });
    };
    const bounds = (r: { min?: number; max?: number; helper?: string }) => [
      r.helper || "",
      typeof r.min === "number" ? t("journeys.bookingQuestions.atLeast", "at least {min}", { min: String(r.min) }) : "",
      typeof r.max === "number" ? t("journeys.bookingQuestions.atMost", "at most {max}", { max: String(r.max) }) : "",
    ].filter(Boolean).join(" · ");
    if (rows.length === 1 && rows[0].key === "single") {
      const r = rows[0];
      const hint = [q.help || "", bounds(r)].filter(Boolean).join(" · ") || null;
      return (
        <Shell q={q} ids={ids} group={false} error={error} help={hint}>
          <input {...common} aria-describedby={[hint ? ids.help : "", error ? ids.error : ""].filter(Boolean).join(" ") || undefined}
            type="text" inputMode="numeric" readOnly={ro} value={rowValue("single")}
            onChange={(e) => setRow("single", e.target.value.replace(/[^\d]/g, ""))}
            onBlur={(e) => { if (e.target.value) setRow("single", clampCount(e.target.value, r.min, r.max)); }} />
        </Shell>
      );
    }
    return (
      <Shell q={q} ids={ids} group={true} error={error} help={help}>
        <div className="jrni-x-rows">
          {rows.map((r) => {
            const rid = base + "-" + r.key;
            const hint = bounds(r);
            return (
              <div className="jrni-x-row" key={r.key}>
                {r.label ? <label htmlFor={rid}>{r.label}</label> : null}
                <input className="jrni-form-input" id={rid} type="text" inputMode="numeric" readOnly={ro}
                  aria-label={r.label ? undefined : q.label} aria-invalid={invalid ? true : undefined} aria-describedby={hint ? rid + "-hint" : undefined}
                  value={rowValue(r.key)}
                  onChange={(e) => setRow(r.key, e.target.value.replace(/[^\d]/g, ""))}
                  onBlur={(e) => { if (e.target.value) setRow(r.key, clampCount(e.target.value, r.min, r.max)); }} />
                {hint ? <span className="jrni-form-help" id={rid + "-hint"}>{hint}</span> : null}
              </div>
            );
          })}
        </div>
      </Shell>
    );
  }

  let control: React.ReactNode;
  if (q.type === "file") {
    control = <FileField q={q} host={host} ids={ids} describedBy={describedBy} invalid={invalid} />;
  } else if (q.type === "select") {
    control = (
      <select {...common} disabled={ro} value={picked[0] || ""} onChange={(e) => set(e.target.value)}>
        <option value="">{t("journeys.walkInJourney.text9", "Choose…")}</option>
        {opts.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    );
  } else if (q.type === "long") {
    control = <textarea {...common} rows={4} readOnly={ro} maxLength={cap} placeholder={q.placeholder || undefined} value={str} onChange={(e) => set(e.target.value)} />;
  } else if (q.type === "number") {
    const input = <input {...common} type="text" inputMode="decimal" readOnly={ro} placeholder={q.placeholder || undefined}
      value={str} onChange={(e) => set(e.target.value.replace(/[^\d.,-]/g, ""))} />;
    control = q.number && q.number.unit ? <div className="jrni-x-unit">{input}<span>{q.number.unit}</span></div> : input;
  } else if (q.type === "date") {
    const min = questionDateMin(q);
    control = <input {...common} type="date" readOnly={ro} min={min} max={(q.date && q.date.latest) || undefined} value={str} onChange={(e) => set(e.target.value)} />;
  } else if (q.type === "datetime") {
    control = <input {...common} type="datetime-local" readOnly={ro} value={str} onChange={(e) => set(e.target.value)} />;
  } else {
    control = <input {...common} type="text" readOnly={ro} maxLength={cap} placeholder={q.placeholder || undefined} value={str} onChange={(e) => set(e.target.value)} />;
  }
  return (
    <Shell q={q} ids={ids} group={false} error={error} help={help}>
      {control}
      {otherBox}
    </Shell>
  );
}
